import { providenceSolutionPallete } from '@core/theming/config/colorPalletes.config';
import { buildThemeSemanticTokens, DEFAULT_SURFACE_PALETTE } from './core/theming/config/theme-semantic.config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { ThemeType } from 'primeng/config';

const defaultSemanticTokens = buildThemeSemanticTokens(600);

const theme: ThemeType = {
  preset: definePreset(Aura, {
    // v5 "premium": rounder shape language across every component — controls ~9px,
    // panels/cards ~14px. One primitive ramp, all themes and tenants inherit.
    primitive: {
      // md drives most controls; at ~31px tall (the density on Load Details)
      // 9px read puffy, so the control tier sits at 7px and the roomier
      // panel/card tiers keep the softer corners.
      borderRadius: {
        none: '0',
        xs: '4px',
        sm: '6px',
        md: '7px',
        lg: '12px',
        xl: '16px'
      }
    },
    semantic: {
      primary: providenceSolutionPallete, // The default palette on initial load
      colorScheme: {
        light: {
          ...defaultSemanticTokens.colorScheme.light,
          text: {
            mutedColor: '{surface.700}',
            hoverMutedColor: '{surface.800}'
          },
          // Shared with ThemingService, which has to restore this ramp when a surface override is taken off.
          surface: DEFAULT_SURFACE_PALETTE.light,
          formField: {
            hoverBorderColor: '{primary.color}',
            // 500, not 400: slate-400 is 2.5 : 1 on white, and in a filter the placeholder ("All companies") is the
            // value the user reads. slate-500 is 4.8 : 1 (2026-10-16 walkthrough).
            placeholderColor: '{surface.500}'
          }
        },
        dark: {
          // Nothing spread from defaultSemanticTokens here: buildThemeSemanticTokens() is
          // palette-shade driven and therefore light-mode only. Its values are re-applied at
          // runtime by ThemingService.switchTheme(), which would overwrite whatever is set
          // below — so the dark scheme owns its tokens outright.
          // v5 "premium": full brand green — the lighter v5 gray ramp carries it
          // without the glare that motivated the old desaturated #3ecf70.
          // Ink sits on the fill at 8.7:1.
          // Dark-scheme only — light mode and every tenant's own palette are untouched.
          // CSS variables, not literals — the same technique focusRing already uses.
          // A literal here would paint Providence green on every white-labelled
          // tenant's dark mode; these tokens resolve per tenant in theme-tokens.css
          // (html.dark:not(.providence-instance) derives them from that owner's own palette).
          // `color` is what text, outlines, links, focus borders and the active tab read, so in dark it is the brand
          // shade that is READABLE on the dark canvas (--accent-text, measured per owner by ThemingService). Solid fills
          // keep the brand shade itself: theme-tokens.css pins the fill tokens to --accent-primary (Dan, 2026-10-02).
          primary: {
            color: 'var(--accent-text)',
            contrastColor: 'var(--accent-ink)',
            hoverColor: 'var(--accent-hover)',
            activeColor: 'var(--accent-muted)'
          },
          // Low-alpha green wash for selected rows and options, rather than a solid fill
          // that would compete with the primary buttons.
          highlight: {
            background: 'color-mix(in srgb, var(--accent-primary) 16%, transparent)',
            focusBackground: 'color-mix(in srgb, var(--accent-primary) 24%, transparent)',
            color: '#ededef',
            focusColor: '#ededef'
          },
          // v5 neutral warm-gray ramp mirroring theme-tokens.css (Bambu-gray family
          // from the approved design preview). Aura maps 950 -> app ground,
          // 900 -> panel/card/overlay, 800 -> control + hover, 700 -> divider,
          // 400 -> muted text, 0 -> primary text. Never #000 and never #fff.
          surface: DEFAULT_SURFACE_PALETTE.dark,
          text: {
            color: '#ededef',
            hoverColor: '#f5f5f7',
            mutedColor: '#a5a7ac',
            hoverMutedColor: '#c9c9cd'
          },
          // Controls sit one step LIGHTER than the panel they're on, so a form well reads
          // as raised rather than punched out. Aura's default puts them at surface.950,
          // which is darker than the card and inverts that relationship.
          // Rest border is 3.39:1 against the control fill (WCAG 1.4.11).
          formField: {
            background: '{surface.800}',
            disabledBackground: '#2a2a2f',
            disabledColor: '#94949a',
            filledBackground: '{surface.800}',
            filledHoverBackground: '{surface.700}',
            filledFocusBackground: '{surface.800}',
            // Fields are filled AND edged. The borderless preview treatment was
            // tried and reverted: with the control fill only 1.14:1 against the
            // panel, the edge was the sole thing separating a field from its
            // background, and dense screens (Items carries 42 controls) dissolved
            // into one flat block. Measured on the v5 tokens: 0.37 alpha = 3.05:1
            // on the control fill and 3.25:1 on the panel, so the edge clears the
            // WCAG 1.4.11 3:1 minimum against BOTH adjacent surfaces. (0.36 was
            // 2.97:1 on the fill — under the line the comment claimed it met.)
            borderColor: 'rgba(255, 255, 255, 0.37)',
            hoverBorderColor: 'rgba(255, 255, 255, 0.52)',
            focusBorderColor: 'var(--accent-primary)',
            color: '#ededef',
            placeholderColor: '#9ea0a6',
            iconColor: '#a5a7ac',
            shadow: 'none'
          }
        }
      },
      focusRing: defaultSemanticTokens.focusRing
    }
  }),
  options: {
    darkModeSelector: '.dark'
  }
};

export default theme;
